import IconButton from '@mui/material/IconButton';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Trash } from 'assets/volvoIcons';
import { CustomSeverityDialog } from 'library/components/controls/dialogs/CustomSeverityDialog';
import { useDialogState } from 'library/hooks/useDialogState';

export type FileRemoveButtonProps = {
	deleteFn: () => Promise<unknown>;
	onDeleted?: () => void | Promise<void>;
	disabled?: boolean;
};

export const FileRemoveButton = ({
	deleteFn,
	onDeleted,
	disabled,
}: FileRemoveButtonProps): React.JSX.Element => {
	const { t } = useTranslation(['commonButton', 'common']);
	const { enqueueSnackbar } = useSnackbar();
	const { open, handleOpen, handleClose } = useDialogState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const handleAccept = useCallback(() => {
		setIsDeleting(true);

		deleteFn()
			.then(async () => {
				enqueueSnackbar(t('common:file-remove-success'), {
					variant: 'success',
				});
				await onDeleted?.();
				handleClose();
			})
			.catch((error) => {
				enqueueSnackbar(
					(error as Error | undefined)?.message ??
						t('common:error-unexpected'),
					{
						variant: 'error',
						style: { whiteSpace: 'pre-line' },
					}
				);
			})
			.finally(() => {
				setIsDeleting(false);
			});
	}, [deleteFn, enqueueSnackbar, handleClose, onDeleted, t]);

	return (
		<>
			<IconButton
				onClick={handleOpen}
				disabled={disabled || isDeleting}
				aria-label={t('commonButton:delete')}>
				<Trash />
			</IconButton>
			{open && (
				<CustomSeverityDialog
					acceptButtonLabel={t('commonButton:yes')}
					declineButtonLabel={t('commonButton:no')}
					content={t('common:file-remove-confirmation')}
					headerText={t('commonButton:delete')}
					isOpen
					onAccept={handleAccept}
					onDecline={handleClose}
					disabledAcceptButton={isDeleting}
					disabledDeclineButton={isDeleting}
					severity="warning"
					loading={isDeleting}
				/>
			)}
		</>
	);
};
